/**
 * Tutors dashboard section: tabbed list with create/edit collapse.
 */

import * as React from 'react';
import * as Icons from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { TutorsDataTable } from '@/components/tutors/inperson/TutorsDataTable';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { CreateTutorView } from '@/components/tutors/inperson/CreateTutorView';
import { EditTutorView } from '@/components/tutors/inperson/EditTutorView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { Select } from '@/components/forms/Select';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import {
  type TutorInPersonTabView as TabView,
  TUTOR_INPERSON_TAB_LABELS as TAB_LABELS,
  TUTOR_INPERSON_TAB_COLORS as TAB_COLORS,
  TUTOR_INPERSON_TAB_ICONS as TAB_ICONS,
} from '@/components/tutors/tutor-configs';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link TutorsSection}. */
interface TutorsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Tutor status tabs shown in the in-person dashboard widget. */
const TAB_ORDER: TabView[] = ['tutors', 'pre_registered', 'to_validate', 'to_reassign'];

/** Dashboard widget: in-person tutors list with create/edit. */
export const TutorsSection: React.FC<TutorsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const { elements: sidebarElements } = useInterfaceElements('sidebar');
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editTutorId, setEditTutorId] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<TabView>('tutors');

  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('tutors');
  const { openRowId, openRow, closeRow } = useDetailsView();

  const handleCreateTutor = () => {
    closeRow();
    setActiveForm('create');
    setEditTutorId(null);
  };

  const handleEditTutor = (id: string) => {
    closeRow();
    setEditTutorId(id);
    setActiveForm('edit');
  };

  /** Close the form and drop the edit id so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditTutorId(null);
  }, []);

  /** Close the form and refresh tutor / onboarding lists. */
  const handleSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditTutorId(null);
    void queryClient.invalidateQueries({ queryKey: ['users', 'tutor'] });
    void queryClient.invalidateQueries({ queryKey: ['users-search', 'tutor'] });
    void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    onSuccess?.();
  }, [queryClient, onSuccess]);

  const tabSelectOptions = React.useMemo(() => {
    const IconsMap = Icons as unknown as Record<string, Icons.LucideIcon>;
    const menuEl = sidebarElements.find((el) => el.elementId === 'menu-tutors');
    const MainIcon = IconsMap[menuEl?.config?.['iconName'] as string] ?? Icons.Circle;
    return TAB_ORDER.map((id) => {
      const Icon = id === 'tutors' ? MainIcon : TAB_ICONS[id];
      return {
        value: id,
        label: TAB_LABELS[id],
        icon: <Icon className="h-4 w-4" />,
        color: TAB_COLORS[id],
      };
    });
  }, [sidebarElements]);

  const tabsNode = (
    <Select
      value={activeTab}
      onChange={(val) => {
        closeForm();
        closeRow();
        setActiveTab(val as TabView);
      }}
      size="sm"
      options={tabSelectOptions}
    />
  );

  /** Stable JSX node for the form — avoids unmounting {@link EditTutorView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (activeForm === 'create') {
      return <CreateTutorView onSuccess={handleSuccess} onClose={closeForm} />;
    }
    if (activeForm === 'edit' && editTutorId) {
      return (
        <EditTutorView
          key={editTutorId}
          tutorUserId={editTutorId}
          onSuccess={handleSuccess}
          onClose={closeForm}
        />
      );
    }
    return null;
  }, [activeForm, editTutorId, handleSuccess, closeForm]);

  return (
    <CollapseReplaceWrapper
      mode="replace"
      isOpen={activeForm !== null}
      collapsePosition="top"
      formContent={formContent}
    >
      <TutorsDataTable
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isSection={true}
        showCreateButton={canCreate}
        layoutWidth={layoutWidth}
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditTutor={handleEditTutor}
        onCreateTutor={handleCreateTutor}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
        toolbarLeft={tabsNode}
      />
    </CollapseReplaceWrapper>
  );
};
